/**
 * API Manager Dashboard
 * 
 * Platform-level API integration catalog with tabs for registry,
 * connections, mappings, and consumers. Execution and monitoring
 * are handled by the Data Flow Manager.
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Tabs,
  Tab,
  Typography,
  Chip
} from '@mui/material';
import {
  Api as ApiIcon,
  Link as LinkIcon,
  SwapHoriz as TransformIcon,
  People as PeopleIcon,
  PlayCircle as PlayCircleIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import ApiRegistryTab from './ApiRegistryTab';
import ConnectionsTab from './ConnectionsTab';
import MappingsTab from './MappingsTab';
import ConsumersTab from './ConsumersTab';
import PlaygroundTab from './PlaygroundTab';
import AskAIPanel from '@/components/AskAIPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`api-manager-tabpanel-${index}`}
      aria-labelledby={`api-manager-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function ApiManagerDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <ApiIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" component="h1">
            API Manager
          </Typography>
          <Chip 
            label="Beta" 
            size="small" 
            color="info"
            sx={{ ml: 1 }}
          />
        </Box>
        <Typography variant="body1" color="text.secondary">
          Platform-level registry for external APIs, field mappings, OAuth connections, and consumers
        </Typography>
      </Box>

      <Alert
        severity="info"
        sx={{ mb: 3 }}
        icon={<TimelineIcon fontSize="inherit" />}
        action={
          <Button color="inherit" size="small" onClick={() => navigate('/dataflow')}>
            Open Data Flow Manager
          </Button>
        }
      >
        Pipeline execution, live data monitoring, and ingest history are now in the Data Flow Manager.
      </Alert>

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="API Manager tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2
          }}
        >
          <Tab
            icon={<ApiIcon />}
            iconPosition="start"
            label="API Registry"
            id="api-manager-tab-0"
            aria-controls="api-manager-tabpanel-0"
          />
          <Tab
            icon={<LinkIcon />}
            iconPosition="start"
            label="Connections"
            id="api-manager-tab-1"
            aria-controls="api-manager-tabpanel-1"
          />
          <Tab
            icon={<TransformIcon />}
            iconPosition="start"
            label="Field Mappings"
            id="api-manager-tab-2"
            aria-controls="api-manager-tabpanel-2"
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Consumers"
            id="api-manager-tab-3"
            aria-controls="api-manager-tabpanel-3"
          />
          <Tab
            icon={<PlayCircleIcon />}
            iconPosition="start"
            label="Playground"
            id="api-manager-tab-4"
            aria-controls="api-manager-tabpanel-4"
          />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          <ApiRegistryTab />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <ConnectionsTab />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <MappingsTab />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <ConsumersTab />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <PlaygroundTab />
        </TabPanel>
      </Paper>

      <AskAIPanel 
        domain="apis"
        title="API Manager AI"
        placeholder="Ask about Kong routes, services, or API management..."
        initialMessage="Hi! I'm your API Manager assistant. I can help you understand Kong configurations, suggest API designs, and troubleshoot routing issues. What would you like to know?"
      />
    </Container>
  );
}
